import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  Folder,
  Search,
  Users as UsersIcon,
  UserPlus,
  X,
  Loader2,
  Crown,
  Settings2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import {
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useGetTeamsListQuery,
  useGetClickupFoldersQuery,
  type TeamListItem,
} from '@/redux/service/teams'
import { useGetEmployeesQuery } from '@/redux/service/employee'
import { useGetUserQuery } from '@/redux/service/userManagement'
import InviteUserModal from '@/components/userManagement/InviteUserModal'

interface TeamFormDialogProps {
  open: boolean
  onClose: () => void
  team?: TeamListItem | null
}

const FieldLabel = ({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string
  children: React.ReactNode
  required?: boolean
}) => (
  <Label
    htmlFor={htmlFor}
    className='text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80 px-1 mb-1.5 flex items-center gap-1.5'
  >
    {children}
    {required && <span className='text-destructive'>*</span>}
  </Label>
)

const TeamFormDialog = ({ open, onClose, team }: TeamFormDialogProps) => {
  const isEdit = !!team
  const [name, setName] = useState('')
  const [folderIds, setFolderIds] = useState<number[]>([])
  const [leader, setLeader] = useState<string>('none')
  const [pm, setPm] = useState<string>('none')
  const [isActive, setIsActive] = useState<boolean>(true)
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [folderSearch, setFolderSearch] = useState('')
  const [manualFolderId, setManualFolderId] = useState('')

  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation()
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation()

  const { data: usersData, refetch: refetchUsers } = useGetUserQuery(
    { page: 1, limit: 500 },
    { skip: !open }
  )
  const { data: employeesData } = useGetEmployeesQuery(
    { page: 1, limit: 500, salarySetup: 1 },
    { skip: !open }
  )

  const usersByEmail = useMemo(() => {
    const map = new Map<string, { id: number; name: string; email: string }>()
    usersData?.data?.forEach(u => {
      if (u.email) map.set(u.email.toLowerCase(), u)
    })
    return map
  }, [usersData])

  const [pendingInvite, setPendingInvite] = useState<{
    name: string
    email: string
    employeeId: number
  } | null>(null)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const lastCreatedEmailRef = useRef<string | null>(null)
  const { data: teamsListData } = useGetTeamsListQuery(undefined, {
    skip: !open,
  })
  const { data: foldersData, isLoading: isFoldersLoading } =
    useGetClickupFoldersQuery({ pending: false }, { skip: !open })

  const folderNameById = useMemo(() => {
    const map = new Map<number, string>()
    foldersData?.data?.forEach(f => {
      map.set(f.folder_id, f.folder_name)
    })
    teamsListData?.data?.forEach(t => {
      t.folders?.forEach(f => {
        if (!map.has(f.folder_id)) map.set(f.folder_id, f.folder_name)
      })
    })
    return map
  }, [foldersData, teamsListData])

  const allFolders = useMemo(() => {
    const list = (foldersData?.data ?? []).slice()
    return list.sort((a, b) => a.folder_name.localeCompare(b.folder_name))
  }, [foldersData])

  const filteredFolders = useMemo(() => {
    const q = folderSearch.trim().toLowerCase()
    if (!q) return allFolders
    return allFolders.filter(
      f =>
        f.folder_name.toLowerCase().includes(q) ||
        String(f.folder_id).includes(q)
    )
  }, [allFolders, folderSearch])

  const allEmployees = useMemo(() => employeesData?.data ?? [], [employeesData])

  const handleLeaderChange = (val: string) => {
    if (!val.startsWith('employee:')) {
      setLeader(val)
      setPendingInvite(null)
      return
    }
    const empId = Number(val.split(':')[1])
    const emp = employeesData?.data?.find(e => e.user_id === empId)
    if (!emp) return
    const existing = emp.user_email
      ? usersByEmail.get(emp.user_email.toLowerCase())
      : undefined
    if (existing) {
      setLeader(`employee:${empId}`)
      setPendingInvite(null)
      return
    }
    setPendingInvite({
      name: emp.user_name,
      email: emp.user_email ?? '',
      employeeId: emp.user_id,
    })
  }

  useEffect(() => {
    const createdEmail = lastCreatedEmailRef.current
    if (!createdEmail) return
    const u = usersByEmail.get(createdEmail.toLowerCase())
    if (u && pendingInvite) {
      setLeader(`employee:${pendingInvite.employeeId}`)
      setPendingInvite(null)
      lastCreatedEmailRef.current = null
    }
  }, [usersByEmail, pendingInvite])

  useEffect(() => {
    if (!open) return
    if (team) {
      setName(team.name)
      setFolderIds(team.folder_ids ?? [])
      setIsActive(Boolean(team.is_active))
      if (team.leader_employee_id != null) {
        setLeader(`employee:${team.leader_employee_id}`)
      } else if (team.leader_user_id != null) {
        setLeader(`user:${team.leader_user_id}`)
      } else {
        setLeader('none')
      }

      if (team.project_manager_id != null) {
        setPm(`employee:${team.project_manager_id}`)
      } else {
        setPm('none')
      }
    } else {
      setName('')
      setFolderIds([])
      setLeader('none')
      setPm('none')
      setIsActive(true)
    }
    setFolderSearch('')
    setManualFolderId('')
    setFolderPickerOpen(false)
  }, [open, team])

  const toggleFolder = (id: number) => {
    setFolderIds(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )
  }

  const removeFolder = (id: number) => {
    setFolderIds(prev => prev.filter(f => f !== id))
  }

  const addManualFolder = () => {
    const trimmed = manualFolderId.trim()
    if (!trimmed) return
    const num = Number(trimmed)
    if (!Number.isFinite(num) || num <= 0) {
      toast.error('Folder id must be a positive number')
      return
    }
    if (folderIds.includes(num)) {
      toast.error('Folder already added')
      return
    }
    setFolderIds(prev => [...prev, num])
    setManualFolderId('')
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Team name is required')
      return
    }
    const [leaderKind, leaderIdStr] = leader.split(':')
    let leaderUserId: number | null = null
    let leaderEmployeeId: number | null = null

    if (leaderKind === 'user') {
      leaderUserId = Number(leaderIdStr)
    } else if (leaderKind === 'employee') {
      leaderEmployeeId = Number(leaderIdStr)
      const emp = employeesData?.data?.find(e => e.user_id === leaderEmployeeId)
      const matchedUser = emp?.user_email
        ? usersByEmail.get(emp.user_email.toLowerCase())
        : undefined
      if (matchedUser) leaderUserId = matchedUser.id
    }

    const pmId = pm.startsWith('employee:') ? Number(pm.split(':')[1]) : null

    try {
      if (isEdit && team) {
        await updateTeam({
          id: team.id,
          data: {
            name: name.trim(),
            folder_ids: folderIds,
            user_id: leaderUserId,
            employee_id: leaderEmployeeId,
            project_manager_id: pmId,
            is_active: isActive,
          },
        }).unwrap()
        toast.success('Team updated')
      } else {
        await createTeam({
          name: name.trim(),
          folder_ids: folderIds,
          is_active: isActive,
          ...(leaderUserId != null && { user_id: leaderUserId }),
          ...(leaderEmployeeId != null && { employee_id: leaderEmployeeId }),
          ...(pmId != null && { project_manager_id: pmId }),
        }).unwrap()
        toast.success('Team created')
      }
      onClose()
    } catch (err) {
      const error = err as { data?: { message?: string }; status?: number }
      if (error?.status === 409) {
        toast.error(
          error?.data?.message ??
            'Folder already claimed by another team or name/leader in use'
        )
      } else {
        toast.error(error?.data?.message ?? 'Failed to save team')
      }
    }
  }

  const saving = isCreating || isUpdating

  return (
    <>
      <Dialog open={open} onOpenChange={o => !o && onClose()}>
        <DialogContent className='max-w-[90vw] md:max-w-[580px] p-0 bg-background border-border overflow-hidden rounded-3xl shadow-2xl flex flex-col max-h-[92vh]'>
          <DialogHeader className='p-8 bg-muted/30 border-b border-border/50 flex-row gap-5 items-center shrink-0'>
            <div className='h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner shrink-0'>
              {isEdit ? (
                <Settings2 className='w-7 h-7' />
              ) : (
                <UsersIcon className='w-7 h-7' />
              )}
            </div>
            <div className='space-y-1'>
              <DialogTitle className='text-2xl font-bold tracking-tight text-foreground'>
                {isEdit ? 'Edit Team Details' : 'Initialize New Team'}
              </DialogTitle>
              <DialogDescription className='text-sm text-muted-foreground'>
                {isEdit
                  ? 'Update your workspace team parameters and leadership settings.'
                  : 'Establish a new team unit and assign core identifiers.'}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div
            className='p-8 space-y-7 overflow-y-auto overflow-x-hidden flex-1 min-h-0 pointer-events-auto isolate'
            onWheel={e => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className='space-y-6'
            >
              {/* Team Name */}
              <div className='space-y-1.5'>
                <FieldLabel htmlFor='team-name' required>
                  Team Name
                </FieldLabel>
                <div className='relative group'>
                  <UsersIcon className='absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary' />
                  <Input
                    id='team-name'
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder='e.g. Creative Dynamics'
                    className='pl-11 h-12 bg-card border-border/50 focus-visible:ring-primary focus-visible:border-primary transition-all rounded-xl shadow-sm'
                  />
                </div>
              </div>

              {/* Leadership Selection */}
              <div className='space-y-1.5'>
                <FieldLabel>Designated Leader</FieldLabel>
                <Select value={leader} onValueChange={handleLeaderChange}>
                  <SelectTrigger className='h-12 bg-card border-border/50 rounded-xl hover:bg-accent/30 transition-all'>
                    <div className='flex items-center gap-2.5'>
                      <Crown className='h-4 w-4 text-amber-500' />
                      <SelectValue placeholder='Unassigned' />
                    </div>
                  </SelectTrigger>
                  <SelectContent className='max-h-[320px] rounded-2xl shadow-xl border-border'>
                    <SelectItem value='none' className='font-medium'>
                      Unassigned
                    </SelectItem>
                    {(usersData?.data?.length ?? 0) > 0 && (
                      <SelectGroup>
                        <SelectSeparator className='my-2' />
                        <SelectLabel className='text-[10px] font-black uppercase tracking-widest text-muted-foreground px-3 py-2'>
                          System Users
                        </SelectLabel>
                        {usersData?.data?.map(u => (
                          <SelectItem
                            key={`user-${u.id}`}
                            value={`user:${u.id}`}
                            className='rounded-lg mx-1'
                          >
                            <div className='flex flex-col'>
                              <span className='font-bold'>{u.name}</span>
                              <span className='text-[10px] opacity-60'>
                                {u.email}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {allEmployees.length > 0 && (
                      <SelectGroup>
                        <SelectSeparator className='my-2' />
                        <SelectLabel className='text-[10px] font-black uppercase tracking-widest text-muted-foreground px-3 py-2'>
                          Workspace Employees
                        </SelectLabel>
                        {allEmployees.map(e => {
                          const hasAccount = e.user_email
                            ? usersByEmail.has(e.user_email.toLowerCase())
                            : false
                          return (
                            <SelectItem
                              key={`employee-${e.user_id}`}
                              value={`employee:${e.user_id}`}
                              className='rounded-lg mx-1'
                            >
                              <div className='flex items-center gap-2'>
                                <div className='flex flex-col min-w-0'>
                                  <span className='font-bold truncate'>
                                    {e.user_name}
                                  </span>
                                  {e.user_email && (
                                    <span className='text-[10px] opacity-60 truncate'>
                                      {e.user_email}
                                    </span>
                                  )}
                                </div>
                                {!hasAccount && (
                                  <span className='text-[8px] font-black uppercase bg-amber-500/10 text-amber-600 px-1.5 py-0.5 rounded-md border border-amber-500/20 shrink-0 ml-auto'>
                                    No Login
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>

                {pendingInvite ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className='rounded-2xl border border-amber-200 bg-amber-50/50 backdrop-blur-sm p-4 mt-3 flex items-start gap-4 shadow-sm'
                  >
                    <div className='h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 shrink-0'>
                      <AlertCircle className='w-5 h-5' />
                    </div>
                    <div className='space-y-3 flex-1'>
                      <div className='space-y-1'>
                        <p className='font-bold text-amber-900 leading-none'>
                          Login Required
                        </p>
                        <p className='text-[11px] text-amber-700 leading-relaxed font-medium'>
                          <strong>{pendingInvite.name}</strong> needs a user
                          account to be designated as a team leader.
                        </p>
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          type='button'
                          size='sm'
                          onClick={() => setInviteModalOpen(true)}
                          className='h-8 bg-amber-600 text-white hover:bg-amber-700 font-bold rounded-lg px-4'
                        >
                          <UserPlus className='w-3.5 h-3.5 mr-2' />
                          Invite Now
                        </Button>
                        <Button
                          type='button'
                          size='sm'
                          variant='ghost'
                          onClick={() => setPendingInvite(null)}
                          className='h-8 text-amber-700 hover:bg-amber-100 font-bold rounded-lg'
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <p className='text-[10px] text-muted-foreground/70 font-medium px-1'>
                    Leaders must possess a valid system login. Employees without
                    accounts can be invited directly.
                  </p>
                )}
              </div>

              {/* Project Manager Selection */}
              <div className='space-y-1.5'>
                <FieldLabel>Project Manager</FieldLabel>
                <Select value={pm} onValueChange={setPm}>
                  <SelectTrigger className='h-12 bg-card border-border/50 rounded-xl hover:bg-accent/30 transition-all'>
                    <div className='flex items-center gap-2.5'>
                      <UserPlus className='h-4 w-4 text-blue-500' />
                      <SelectValue placeholder='Unassigned' />
                    </div>
                  </SelectTrigger>
                  <SelectContent className='max-h-[320px] rounded-2xl shadow-xl border-border'>
                    <SelectItem value='none' className='font-medium'>
                      Unassigned
                    </SelectItem>
                    {allEmployees.length > 0 && (
                      <SelectGroup>
                        <SelectSeparator className='my-2' />
                        <SelectLabel className='text-[10px] font-black uppercase tracking-widest text-muted-foreground px-3 py-2'>
                          Workspace Employees
                        </SelectLabel>
                        {allEmployees.map(e => (
                          <SelectItem
                            key={`pm-${e.user_id}`}
                            value={`employee:${e.user_id}`}
                            className='rounded-lg mx-1'
                          >
                            <div className='flex flex-col min-w-0'>
                              <span className='font-bold truncate'>
                                {e.user_name}
                              </span>
                              {e.user_email && (
                                <span className='text-[10px] opacity-60 truncate'>
                                  {e.user_email}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Folder Connection */}
              <div className='space-y-1.5'>
                <FieldLabel>ClickUp Integration</FieldLabel>
                <Popover
                  open={folderPickerOpen}
                  onOpenChange={setFolderPickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      className='w-full justify-between h-12 bg-card border-border/50 rounded-xl hover:bg-accent/30 transition-all px-4 group'
                      disabled={isFoldersLoading && allFolders.length === 0}
                    >
                      <div className='flex items-center gap-3'>
                        <Folder className='w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors' />
                        <span className='font-medium text-sm'>
                          {isFoldersLoading && allFolders.length === 0
                            ? 'Fetching folders...'
                            : folderIds.length === 0
                              ? 'Associate Folders'
                              : `${folderIds.length} Linked ${folderIds.length === 1 ? 'Folder' : 'Folders'}`}
                        </span>
                      </div>
                      <ChevronsUpDown className='w-4 h-4 text-muted-foreground opacity-50' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className='w-[var(--radix-popover-trigger-width)] p-0 rounded-2xl overflow-auto shadow-2xl border-border bg-popover'
                    align='start'
                  >
                    <div className='p-3 border-b border-border/50 bg-muted/30'>
                      <div className='relative group'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground group-focus-within:text-primary' />
                        <Input
                          autoFocus
                          placeholder='Search system folders...'
                          value={folderSearch}
                          onChange={e => setFolderSearch(e.target.value)}
                          className='pl-9 h-9 bg-background border-border/50 rounded-lg text-xs'
                        />
                      </div>
                    </div>
                    <div
                      className='max-h-[300px] overflow-y-auto py-1 pointer-events-auto'
                      onWheel={e => e.stopPropagation()}
                    >
                      {filteredFolders.length === 0 ? (
                        <div className='py-12 px-4 text-center space-y-2'>
                          <Folder className='w-8 h-8 text-muted-foreground/20 mx-auto' />
                          <p className='text-xs text-muted-foreground font-medium'>
                            No available folders found
                          </p>
                        </div>
                      ) : (
                        filteredFolders.map(f => {
                          const selected = folderIds.includes(f.folder_id)
                          return (
                            <button
                              key={f.folder_id}
                              type='button'
                              onClick={() => toggleFolder(f.folder_id)}
                              className={cn(
                                'w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-accent transition-all',
                                selected && 'bg-primary/5'
                              )}
                            >
                              <div
                                className={cn(
                                  'h-5 w-5 rounded-md border-2 transition-all flex items-center justify-center',
                                  selected
                                    ? 'bg-primary border-primary scale-110 shadow-sm'
                                    : 'border-border bg-background'
                                )}
                              >
                                {selected && (
                                  <Check className='w-3 h-3 text-white' />
                                )}
                              </div>
                              <div className='flex flex-col min-w-0'>
                                <span
                                  className={cn(
                                    'font-bold leading-none',
                                    selected
                                      ? 'text-primary'
                                      : 'text-foreground'
                                  )}
                                >
                                  {f.folder_name}
                                </span>
                                <span className='text-[10px] font-mono text-muted-foreground/60 mt-1 uppercase tracking-tighter'>
                                  ID: {f.folder_id}
                                </span>
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                    <div className='p-3 border-t border-border/50 bg-muted/10 flex gap-2'>
                      <Input
                        placeholder='Enter Folder ID'
                        value={manualFolderId}
                        onChange={e => setManualFolderId(e.target.value)}
                        className='h-9 bg-background border-border/50 rounded-lg text-xs'
                      />
                      <Button
                        type='button'
                        variant='secondary'
                        size='sm'
                        onClick={addManualFolder}
                        className='h-9 font-bold text-xs rounded-lg px-4'
                      >
                        Add
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                <AnimatePresence>
                  {folderIds.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className='flex flex-wrap gap-2 pt-3'
                    >
                      {folderIds.map(id => (
                        <motion.div
                          layout
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.8, opacity: 0 }}
                          key={id}
                          className='inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-xl bg-card border border-border shadow-sm group hover:border-destructive/30 transition-all'
                        >
                          <Folder className='w-3.5 h-3.5 text-primary/60' />
                          <span className='text-[11px] font-bold text-foreground'>
                            {folderNameById.get(id) ?? `#${id}`}
                          </span>
                          <button
                            type='button'
                            onClick={() => removeFolder(id)}
                            className='h-5 w-5 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-destructive hover:text-white transition-all shadow-sm'
                          >
                            <X className='w-3 h-3' />
                          </button>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Status Switch */}
              <div className='flex items-center justify-between p-5 rounded-2xl bg-card border border-border/50 shadow-sm'>
                <div className='space-y-1'>
                  <Label className='text-sm font-bold text-foreground'>
                    Operational Status
                  </Label>
                  <p className='text-[10px] text-muted-foreground font-medium leading-relaxed max-w-[280px]'>
                    Deactivating a team preserves its historical data while
                    removing it from active selection menus.
                  </p>
                </div>
                <button
                  type='button'
                  role='switch'
                  aria-checked={isActive}
                  onClick={() => setIsActive(v => !v)}
                  className={cn(
                    'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-all duration-300 focus:outline-none shadow-inner',
                    isActive ? 'bg-primary' : 'bg-muted'
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-5 w-5 transform rounded-full bg-white shadow-xl transition-transform duration-300',
                      isActive ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
            </motion.div>
          </div>

          <DialogFooter className='p-8 pt-6 border-t border-border/50 bg-muted/10 flex gap-4 shrink-0'>
            <Button
              variant='ghost'
              onClick={onClose}
              className='flex-1 h-12 font-bold text-muted-foreground hover:text-foreground hover:bg-card rounded-xl transition-all'
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className='flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl shadow-[0_12px_24px_-8px_rgba(var(--primary),0.3)] hover:shadow-[0_12px_32px_-4px_rgba(var(--primary),0.4)] transition-all hover:scale-[1.02] active:scale-[0.98]'
            >
              {saving ? (
                <div className='flex items-center gap-2'>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  <span>Processing...</span>
                </div>
              ) : (
                <span className='flex items-center gap-2'>
                  {isEdit ? 'Save Parameters' : 'Create Team'}
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteUserModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        {...(pendingInvite && {
          prefill: {
            name: pendingInvite.name,
            email: pendingInvite.email,
          },
        })}
        onCreated={({ email }) => {
          lastCreatedEmailRef.current = email
          refetchUsers()
        }}
      />
    </>
  )
}

export default TeamFormDialog
